import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { Color, IconName, InputTheme } from '../../enums';
import { useBasketSummaryItems } from '../../hooks';
import { BasketItem, DiscountCode, UseBasketSummaryItems } from '../../interfaces';
import { Overlay } from '../../theme';
import { UseState } from '../../types';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { Input } from '../input/input.component';

import { 
  ApplyButton,
  ItemsContainer, 
  BasketSidePanelWrapper, 
  CartContainer, 
  CloseButton, 
  Container, 
  DetailsContainer, 
  DiscountCodesContainer, 
  DiscountContainer, 
  EmptyCartContainer, 
  IconContainer, 
  StyledArtworkContainer, 
  StyledBasketSummary, 
  StyledChooseBox, 
  SummaryContainer, 
  Title,
  ApplyButtonContainer,
  CartInnerContainer
} from './basketSidePanel.styled';
import { BasketSidePanelProps } from './basketSidePanel.types';

export const BasketSidePanel: React.FC<BasketSidePanelProps> = (props: BasketSidePanelProps): JSX.Element => {
  const { 
    items, 
    basePrice,
    finalPrice,
    deliveryStartPrice,
    discountsCodes,
    discount,
    onDiscountCodeEnter, 
    onSubmitButtonClick, 
    onRemoveDiscountCode, 
    onRemoveItem,
    onClose,
    visible = true,
    validationMessage,
    className 
  }: BasketSidePanelProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { basketSummaryItems }: UseBasketSummaryItems = useBasketSummaryItems(
    basePrice, finalPrice, deliveryStartPrice, discount
  );
  const [isExpanded, setIsExpanded]: UseState<boolean> = useState<boolean>(visible);
  const [discountInputValue, setDiscountInputValue]: UseState<string> = useState<string>('');
  const [isDiscountValid, setIsDiscountValid]: UseState<boolean> = useState<boolean>(true);

  const onCloseAction: () => void = (): void => {
    setIsExpanded(!isExpanded);
    setIsDiscountValid(true);
    onClose();
  };

  const onDiscountApplyAction: () => void = (): void => {
    if (discountInputValue === '') {
      setIsDiscountValid(false);
    } else {
      onDiscountCodeEnter(discountInputValue);
      setIsDiscountValid(true);
    }
    setDiscountInputValue('');
  };

  useEffect((): void => setIsExpanded(visible), [visible]);

  return (
    <BasketSidePanelWrapper className={className} id='basket-side-panel'>
      <Container $isExpanded={isExpanded}> 
        <CloseButton onClick={onCloseAction}>
          <Icon name={IconName.Close} color={Color.Black} size={24} />
        </CloseButton>
        {items.length ? (
          <CartContainer>
            <CartInnerContainer>
              <DetailsContainer>
                <Title>{t('onarte.ui.basketSidePanel.detailsContainer.title')}</Title>
                <ItemsContainer id='basket-side-panel-list'>
                  {items.map((item: BasketItem, index: number): JSX.Element => (
                    <StyledArtworkContainer 
                      artworkDetails={{
                        ...item.artworkDetails
                      }} 
                      onRemoveItem={(): void => onRemoveItem(item.id)}
                      key={item.id}
                      index={index}
                    />
                  ))}
                </ItemsContainer>
                <DiscountContainer>
                  <Input 
                    label={t('onarte.ui.basketSidePanel.discountContainer.input.label')} 
                    inputTheme={InputTheme.Small} 
                    labelSecondBackground={Color.Gray200}
                    value={discountInputValue}
                    onChange={(value: string): void => setDiscountInputValue(value)}
                    validationMessage={isDiscountValid ? validationMessage : t('onarte.ui.basketSidePanel.input.validationMessage')}
                  />
                  <ApplyButtonContainer>
                    <ApplyButton onClick={onDiscountApplyAction}>
                      {t('onarte.ui.basketSidePanel.discountContainer.input.button')}
                    </ApplyButton>
                  </ApplyButtonContainer>
                </DiscountContainer>
                {!!discountsCodes.length && (
                  <DiscountCodesContainer>
                    {discountsCodes.map((discountCode: DiscountCode): JSX.Element => (
                      <StyledChooseBox 
                        title={discountCode.value} 
                        subtitle={`(${discountCode.effect})`}
                        icon={IconName.Promotion} 
                        iconSize={50}
                        onRemove={(): void => onRemoveDiscountCode(discountCode.value)}
                        key={discountCode.value}
                      />
                    ))}
                  </DiscountCodesContainer>
                )}
              </DetailsContainer>
              <SummaryContainer>
                <StyledBasketSummary items={basketSummaryItems} />
                <Button label={t('onarte.ui.basketSidePanel.basketSummary.button.label')} onClick={onSubmitButtonClick} fullWidth />
              </SummaryContainer>
            </CartInnerContainer>
          </CartContainer>
        ) : (
          <EmptyCartContainer>
            <IconContainer>
              <Icon name={IconName.Image} color={Color.Black} size={50} />
            </IconContainer>
            {t('onarte.ui.basketSidePanel.emptyCart.message')}
          </EmptyCartContainer>
        )}
      </Container>
      <Overlay $isVisible={isExpanded} onClick={onCloseAction} />
    </BasketSidePanelWrapper>
  );
};
